import type { ContactOut, CustomerOut } from "@/api";
import { LockIcon } from "@/components/icons";
import { otherPerson, present } from "@/lib/contactPerson";

import styles from "./customer.module.css";

/** `tel:` wants digits and a leading plus — „06151 000000" dials as written. */
function telHref(phone: string): string {
  return `tel:${phone.replace(/[^\d+]/g, "")}`;
}

function ContactFact({ contact }: { contact: ContactOut }) {
  const name = present(contact.name);
  const email = present(contact.email);
  const phone = present(contact.phone);
  return (
    <div>
      <dt className="label">Ansprechpartner</dt>
      <dd className={styles.contact}>
        {name !== null ? <span>{name}</span> : null}
        {email !== null ? (
          <a className="inline-link" href={`mailto:${email}`}>
            {email}
          </a>
        ) : null}
        {phone !== null ? (
          <a className="inline-link tabular" href={telHref(phone)}>
            {phone}
          </a>
        ) : null}
      </dd>
    </div>
  );
}

interface CustomerCardProps {
  customer: CustomerOut;
  /** Who wrote the enquiry (`CaseRecordOut.contact`); `null` or absent: nobody is named. */
  contact?: ContactOut | null;
}

/**
 * The customer as a card: the company leads where there is one and the person
 * stands under it (the mockup's „Familie Neumann / Sabine Neumann"), then the
 * JTL number and — in words, with a lock — „in JTL gesperrt"; the address in
 * its two lines, the ways to reach them, and the contact person when she is
 * somebody else. No hand-written copy of anything: it prints `CustomerOut`.
 *
 * Only what is there is printed. „Weitere Vorgänge" is not the customer's but
 * the record's (it counts *my* Vorgänge and leads to the register), and
 * stands under the card in the fact sheet.
 */
export function CustomerCard({ customer, contact = null }: CustomerCardProps) {
  const company = present(customer.company);
  const street = present(customer.street);
  const extra = present(customer.addressExtra);
  const place = [present(customer.postalCode), present(customer.city)].filter((part) => part !== null).join(" ");
  const customerNo = present(customer.customerNo);
  const email = present(customer.email);
  const phone = present(customer.phone);
  const mobile = present(customer.mobile);
  const shownContact = contact !== null && otherPerson(customer, contact) && hasAnything(contact) ? contact : null;
  const hasFacts = email !== null || phone !== null || mobile !== null || shownContact !== null;
  return (
    <div className={styles.card}>
      <div className={styles.who}>
        <p className={styles.name}>{company ?? customer.displayName}</p>
        {company !== null ? <p className={styles.sub}>{customer.displayName}</p> : null}
        {customerNo !== null ? <p className={styles.customerNo}>Kd.-Nr. {customerNo}</p> : null}
        {customer.jtlBlocked ? (
          <p className={styles.blocked}>
            <LockIcon size={12} />
            in JTL gesperrt
          </p>
        ) : null}
      </div>
      {street !== null || extra !== null || place !== "" ? (
        <div className={styles.address}>
          {street !== null ? <span>{street}</span> : null}
          {extra !== null ? <span>{extra}</span> : null}
          {place !== "" ? <span>{place}</span> : null}
        </div>
      ) : null}
      {hasFacts ? (
        <dl className={styles.facts}>
          {email !== null ? (
            <div>
              <dt className="label">E-Mail</dt>
              <dd>
                <a className="inline-link" href={`mailto:${email}`}>
                  {email}
                </a>
              </dd>
            </div>
          ) : null}
          {phone !== null ? (
            <div>
              <dt className="label">Telefon</dt>
              <dd>
                <a className="inline-link tabular" href={telHref(phone)}>
                  {phone}
                </a>
              </dd>
            </div>
          ) : null}
          {mobile !== null ? (
            <div>
              <dt className="label">Mobil</dt>
              <dd>
                <a className="inline-link tabular" href={telHref(mobile)}>
                  {mobile}
                </a>
              </dd>
            </div>
          ) : null}
          {shownContact !== null ? <ContactFact contact={shownContact} /> : null}
        </dl>
      ) : null}
    </div>
  );
}

function hasAnything(contact: ContactOut): boolean {
  return present(contact.name) !== null || present(contact.email) !== null || present(contact.phone) !== null;
}
